import { useMemo, useState } from "react";
import { Calculator, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const fmtINR = (n) =>
  "₹" +
  Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const calcEMI = (P, annualRate, years) => {
  const n = years * 12;
  const r = annualRate / 12 / 100;
  if (r === 0) return { emi: P / n, total: P, interest: 0, months: n };
  const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n;
  return { emi, total, interest: total - P, months: n };
};

const Slider = ({ label, value, min, max, step, onChange, format, suffix, testId }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-400 font-mono">
        {label}
      </label>
      <span
        data-testid={`${testId}-value`}
        className="font-mono text-white text-sm font-semibold"
      >
        {format ? format(value) : value}
        {suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testId}
      className="w-full h-2 bg-[#1a1a1a] rounded-full appearance-none cursor-pointer accent-[#dc2626]"
      style={{
        background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${
          ((value - min) / (max - min)) * 100
        }%, #1a1a1a ${((value - min) / (max - min)) * 100}%, #1a1a1a 100%)`,
      }}
    />
    <div className="flex justify-between mt-1 text-[10px] font-mono text-neutral-600 uppercase tracking-widest">
      <span>
        {format ? format(min) : min}
        {suffix}
      </span>
      <span>
        {format ? format(max) : max}
        {suffix}
      </span>
    </div>
  </div>
);

export default function EMICalculator({ compact = false }) {
  const [amount, setAmount] = useState(2500000);
  const [rate, setRate] = useState(9.5);
  const [tenure, setTenure] = useState(20);

  const { emi, total, interest } = useMemo(
    () => calcEMI(amount, rate, tenure),
    [amount, rate, tenure]
  );

  const principalPct = (amount / total) * 100;

  return (
    <div
      data-testid="emi-calculator"
      className="border border-white/10 rounded-2xl bg-[#0f0f0f] p-5 sm:p-10"
    >
      <div className="flex items-center gap-3 mb-6 sm:mb-8">
        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-lg bg-[#dc2626]/10 border border-[#dc2626]/20 flex items-center justify-center text-[#dc2626] flex-shrink-0">
          <Calculator className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase text-[#dc2626]">
            EMI Calculator
          </div>
          <h3 className="font-display font-bold text-lg sm:text-2xl text-white tracking-tight">
            Plan your loan EMI
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8">
        <div className="lg:col-span-3 space-y-6 sm:space-y-7">
          <Slider
            label="Loan Amount"
            value={amount}
            min={100000}
            max={20000000}
            step={50000}
            onChange={setAmount}
            format={fmtINR}
            testId="emi-amount"
          />
          <Slider
            label="Interest Rate"
            value={rate}
            min={6}
            max={20}
            step={0.1}
            onChange={setRate}
            suffix=" %"
            testId="emi-rate"
          />
          <Slider
            label="Tenure"
            value={tenure}
            min={1}
            max={30}
            step={1}
            onChange={setTenure}
            suffix=" yrs"
            testId="emi-tenure"
          />
        </div>

        <div className="lg:col-span-2 rounded-xl bg-gradient-to-br from-[#dc2626]/10 via-transparent to-[#1d4ed8]/10 border border-white/10 p-6 flex flex-col justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-neutral-500">
              Monthly EMI
            </div>
            <div
              data-testid="emi-monthly"
              className="mt-2 font-display font-bold text-3xl sm:text-5xl text-white tracking-tighter"
            >
              {fmtINR(emi)}
            </div>

            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-400">Principal</span>
                <span
                  data-testid="emi-principal"
                  className="font-mono text-white"
                >
                  {fmtINR(amount)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-400">Total Interest</span>
                <span
                  data-testid="emi-interest"
                  className="font-mono text-[#dc2626]"
                >
                  {fmtINR(interest)}
                </span>
              </div>
              <div className="h-px bg-white/10 my-3" />
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-300 font-semibold">Total Payment</span>
                <span
                  data-testid="emi-total"
                  className="font-mono text-white font-semibold"
                >
                  {fmtINR(total)}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-2">
                Break-up
              </div>
              <div className="h-3 rounded-full overflow-hidden bg-[#1a1a1a] flex">
                <div
                  className="bg-[#1d4ed8]"
                  style={{ width: `${principalPct}%` }}
                />
                <div
                  className="bg-[#dc2626]"
                  style={{ width: `${100 - principalPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 text-[10px] font-mono text-neutral-500 uppercase">
                <span>
                  <span className="inline-block h-2 w-2 bg-[#1d4ed8] rounded-sm mr-1.5" />
                  Principal
                </span>
                <span>
                  <span className="inline-block h-2 w-2 bg-[#dc2626] rounded-sm mr-1.5" />
                  Interest
                </span>
              </div>
            </div>
          </div>

          {!compact && (
            <Link
              to="/request-access"
              data-testid="emi-apply-cta"
              className="mt-6 inline-flex items-center justify-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white px-5 py-3 font-semibold"
            >
              Apply for this loan <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>

      <p className="mt-6 text-xs text-neutral-500 font-mono">
        * Indicative only. Actual rates and EMI depend on lender, credit profile
        and tenure.
      </p>
    </div>
  );
}
